import { canGrantBasicOrganizationAccess } from "./connection.js";

export const organizationRoleIds = [
  "organizationOwner",
  "organizationAdmin",
  "communicationsAdmin",
  "eventsAdmin",
  "eventCheckInStaff",
  "financeAdmin",
  "spacesAdmin",
  "volunteerCoordinator",
  "connectedPerson",
] as const;

export const organizationPermissionIds = [
  "organization.readPrivate",
  "organization.updateProfile",
  "organization.manageCampuses",
  "connections.review",
  "connections.approve",
  "roles.read",
  "roles.assign",
  "roles.assignPrivileged",
  "people.disableAccount",
  "content.manage",
  "announcements.create",
  "announcements.publish",
  "events.manage",
  "events.checkIn",
  "finance.read",
  "finance.refund",
  "spaces.manage",
  "volunteers.manage",
  "directory.affiliations.manage",
  "settings.manage",
  "billing.read",
  "support.manage",
] as const;

export const organizationRoleAssignmentStatuses = ["active", "revoked"] as const;

export const organizationRoleAssignmentSources = [
  "development_seed",
  "migration_hvicc",
  "system_bootstrap",
  "trusted_backend",
] as const;

export const platformSuperAdminRoleId = "platform_super_admin" as const;
export const platformSupportRoleId = "platform_support" as const;
export const platformRoleIds = [platformSuperAdminRoleId, platformSupportRoleId] as const;

export type OrganizationRoleId = (typeof organizationRoleIds)[number];
export type OrganizationPermissionId = (typeof organizationPermissionIds)[number];
export type OrganizationRoleAssignmentStatus = (typeof organizationRoleAssignmentStatuses)[number];
export type OrganizationRoleAssignmentSource = (typeof organizationRoleAssignmentSources)[number];
export type PlatformRoleId = (typeof platformRoleIds)[number];

export type OrganizationRoleAssignmentMigrationMetadata = {
  legacyAdminFlag: boolean;
  legacyFirebaseUid: string | null;
  legacySource: "hvicc_flutterflow_users" | null;
  migratedAt: unknown | null;
};

export type OrganizationRoleAssignmentRecord = {
  organizationId: string;
  personId: string;
  roleIds: OrganizationRoleId[];
  status: OrganizationRoleAssignmentStatus;
  assignedAt: unknown;
  assignedBy: string;
  updatedAt: unknown;
  revokedAt: unknown | null;
  revokedBy: string | null;
  source: OrganizationRoleAssignmentSource;
  migration: OrganizationRoleAssignmentMigrationMetadata;
};

export type OrganizationRoleAssignmentAuditAction = "assigned" | "revoked";

export type OrganizationRoleAssignmentAuditEvent = {
  action: OrganizationRoleAssignmentAuditAction;
  actorPersonId: string;
  createdAt: unknown;
  eventId: string;
  organizationId: string;
  roleIds: OrganizationRoleId[];
  source: OrganizationRoleAssignmentSource;
  targetPersonId: string;
};

export type PlatformRoleRecord = {
  uid?: string;
  personId?: string;
  email?: string;
  role: PlatformRoleId;
  status: "active" | "revoked";
};

export type AuthorizationPrincipal = {
  uid: string;
};

export type OrganizationAuthorizationContext = {
  activeConnection: unknown;
  organization: unknown;
  person: unknown;
  principal: AuthorizationPrincipal | undefined;
  roleAssignment: OrganizationRoleAssignmentRecord | undefined;
};

export type RoleAssignmentAuthorizationDecision =
  | {
      allowed: true;
    }
  | {
      allowed: false;
      reason:
        | "actor_lacks_assignment_authority"
        | "invalid_role"
        | "last_owner_revoke"
        | "missing_active_connection"
        | "missing_principal";
    };

export const organizationRolePermissionMap: Record<
  OrganizationRoleId,
  readonly OrganizationPermissionId[]
> = {
  connectedPerson: ["organization.readPrivate"],
  communicationsAdmin: [
    "organization.readPrivate",
    "content.manage",
    "announcements.create",
    "announcements.publish",
    "support.manage",
  ],
  eventCheckInStaff: ["organization.readPrivate", "events.checkIn"],
  eventsAdmin: ["organization.readPrivate", "events.manage", "events.checkIn"],
  financeAdmin: ["organization.readPrivate", "finance.read", "billing.read"],
  organizationAdmin: [
    "organization.readPrivate",
    "organization.updateProfile",
    "organization.manageCampuses",
    "connections.review",
    "connections.approve",
    "roles.read",
    "roles.assign",
    "people.disableAccount",
    "content.manage",
    "announcements.create",
    "announcements.publish",
    "events.manage",
    "events.checkIn",
    "spaces.manage",
    "volunteers.manage",
    "directory.affiliations.manage",
    "settings.manage",
    "support.manage",
  ],
  organizationOwner: [
    "organization.readPrivate",
    "organization.updateProfile",
    "organization.manageCampuses",
    "connections.review",
    "connections.approve",
    "roles.read",
    "roles.assign",
    "roles.assignPrivileged",
    "people.disableAccount",
    "content.manage",
    "announcements.create",
    "announcements.publish",
    "events.manage",
    "events.checkIn",
    "finance.read",
    "finance.refund",
    "spaces.manage",
    "volunteers.manage",
    "directory.affiliations.manage",
    "settings.manage",
    "billing.read",
    "support.manage",
  ],
  spacesAdmin: ["organization.readPrivate", "spaces.manage"],
  volunteerCoordinator: ["organization.readPrivate", "volunteers.manage"],
};

export const privilegedOrganizationRoleIds = organizationRoleIds.filter(
  (roleId) => roleId !== "connectedPerson",
) as Exclude<OrganizationRoleId, "connectedPerson">[];

export const protectedOrganizationRoleIds = [
  "organizationOwner",
  "organizationAdmin",
  "financeAdmin",
] as const satisfies readonly OrganizationRoleId[];

export function organizationRoleAssignmentDocumentPath(
  organizationId: string,
  personId: string,
): string {
  return `organizations/${organizationId}/roleAssignments/${personId}`;
}

export function organizationRoleAssignmentEventDocumentPath(
  organizationId: string,
  eventId: string,
): string {
  return `organizations/${organizationId}/roleAssignmentEvents/${eventId}`;
}

export function buildOrganizationRoleAssignment(input: {
  assignedBy: string;
  migration?: Partial<OrganizationRoleAssignmentMigrationMetadata>;
  organizationId: string;
  personId: string;
  roleIds: readonly OrganizationRoleId[];
  source: OrganizationRoleAssignmentSource;
  timestamp: unknown;
}): OrganizationRoleAssignmentRecord {
  const roleIds = normalizeOrganizationRoleIds(input.roleIds);

  return {
    assignedAt: input.timestamp,
    assignedBy: input.assignedBy,
    migration: {
      legacyAdminFlag: false,
      legacyFirebaseUid: null,
      legacySource: null,
      migratedAt: null,
      ...input.migration,
    },
    organizationId: input.organizationId,
    personId: input.personId,
    revokedAt: null,
    revokedBy: null,
    roleIds,
    source: input.source,
    status: "active",
    updatedAt: input.timestamp,
  };
}

export function buildRevokedOrganizationRoleAssignment(
  assignment: OrganizationRoleAssignmentRecord,
  input: {
    revokedBy: string;
    timestamp: unknown;
  },
): OrganizationRoleAssignmentRecord {
  return {
    ...assignment,
    revokedAt: input.timestamp,
    revokedBy: input.revokedBy,
    roleIds: [],
    status: "revoked",
    updatedAt: input.timestamp,
  };
}

export function buildOrganizationRoleAssignmentAuditEvent(input: {
  action: OrganizationRoleAssignmentAuditAction;
  actorPersonId: string;
  eventId?: string;
  organizationId: string;
  roleIds: readonly OrganizationRoleId[];
  source: OrganizationRoleAssignmentSource;
  targetPersonId: string;
  timestamp: unknown;
}): OrganizationRoleAssignmentAuditEvent {
  const roleIds = normalizeOrganizationRoleIds(input.roleIds);
  const eventId =
    input.eventId ?? `${input.targetPersonId}_${input.action}_${roleIds.join("_") || "none"}`;

  return {
    action: input.action,
    actorPersonId: input.actorPersonId,
    createdAt: input.timestamp,
    eventId,
    organizationId: input.organizationId,
    roleIds,
    source: input.source,
    targetPersonId: input.targetPersonId,
  };
}

export function hasOrganizationPermission(
  context: OrganizationAuthorizationContext,
  permissionId: OrganizationPermissionId,
): boolean {
  return permissionsForOrganizationAuthorizationContext(context).includes(permissionId);
}

export function permissionsForOrganizationAuthorizationContext(
  context: OrganizationAuthorizationContext,
): OrganizationPermissionId[] {
  return permissionsForOrganizationRoles(effectiveOrganizationRoleIds(context));
}

export function effectiveOrganizationRoleIds(
  context: OrganizationAuthorizationContext,
): OrganizationRoleId[] {
  if (
    !context.principal ||
    !isActiveGlobalPerson(context.person, context.principal.uid) ||
    !isActiveOrganization(context.organization) ||
    !canGrantBasicOrganizationAccess(context.activeConnection)
  ) {
    return [];
  }

  const roleIds = new Set<OrganizationRoleId>(["connectedPerson"]);
  const assignment = context.roleAssignment;

  if (
    assignment?.status === "active" &&
    assignment.personId === context.principal.uid &&
    assignment.organizationId === organizationIdFromRecord(context.organization)
  ) {
    for (const roleId of assignment.roleIds) {
      if (isOrganizationRoleId(roleId)) {
        roleIds.add(roleId);
      }
    }
  }

  return [...roleIds];
}

export function permissionsForOrganizationRoles(
  roleIds: readonly OrganizationRoleId[],
): OrganizationPermissionId[] {
  const permissions = new Set<OrganizationPermissionId>();

  for (const roleId of roleIds) {
    for (const permission of organizationRolePermissionMap[roleId]) {
      permissions.add(permission);
    }
  }

  return [...permissions];
}

export function authorizeOrganizationRoleAssignmentChange(input: {
  action: "assign" | "revoke";
  actorContext: OrganizationAuthorizationContext;
  existingOwnerPersonIds: readonly string[];
  roleIds: readonly OrganizationRoleId[];
  targetPersonId: string;
}): RoleAssignmentAuthorizationDecision {
  if (!input.actorContext.principal) {
    return { allowed: false, reason: "missing_principal" };
  }

  if (!canGrantBasicOrganizationAccess(input.actorContext.activeConnection)) {
    return { allowed: false, reason: "missing_active_connection" };
  }

  if (input.roleIds.length === 0 || input.roleIds.some((roleId) => !isOrganizationRoleId(roleId))) {
    return { allowed: false, reason: "invalid_role" };
  }

  const touchesProtectedRole = input.roleIds.some((roleId) =>
    protectedOrganizationRoleIds.includes(roleId as (typeof protectedOrganizationRoleIds)[number]),
  );
  const requiredPermission: OrganizationPermissionId = touchesProtectedRole
    ? "roles.assignPrivileged"
    : "roles.assign";

  if (!hasOrganizationPermission(input.actorContext, requiredPermission)) {
    return { allowed: false, reason: "actor_lacks_assignment_authority" };
  }

  if (
    input.action === "revoke" &&
    input.roleIds.includes("organizationOwner") &&
    input.existingOwnerPersonIds.filter((personId) => personId !== input.targetPersonId).length ===
      0
  ) {
    return { allowed: false, reason: "last_owner_revoke" };
  }

  return { allowed: true };
}

export function hasTrustedPlatformSuperAdminAuthority(input: {
  platformRole: PlatformRoleRecord | undefined;
  principal: AuthorizationPrincipal | undefined;
}): boolean {
  if (!input.principal || !isRecord(input.platformRole)) {
    return false;
  }

  const roleUid = input.platformRole.uid ?? input.platformRole.personId;

  return (
    roleUid === input.principal.uid &&
    input.platformRole.role === platformSuperAdminRoleId &&
    input.platformRole.status === "active"
  );
}

export function hasTrustedPlatformSupportAuthority(input: {
  platformRole: PlatformRoleRecord | undefined;
  principal: AuthorizationPrincipal | undefined;
}): boolean {
  if (!input.principal || !isRecord(input.platformRole)) {
    return false;
  }

  const roleUid = input.platformRole.uid ?? input.platformRole.personId;

  return (
    roleUid === input.principal.uid &&
    platformRoleIds.includes(input.platformRole.role) &&
    input.platformRole.status === "active"
  );
}

export function validateOrganizationRoleAssignmentRecord(assignment: unknown): {
  errors: string[];
  valid: boolean;
} {
  const errors: string[] = [];

  if (!isRecord(assignment)) {
    return { errors: ["Role assignment must be an object."], valid: false };
  }

  const organizationId = stringValue(assignment.organizationId);
  const personId = stringValue(assignment.personId);

  if (!isSafeDocumentId(organizationId)) {
    errors.push("Role assignment organizationId must be Firestore-safe.");
  }

  if (!isSafeDocumentId(personId)) {
    errors.push("Role assignment personId must be a Firestore-safe document ID.");
  }

  const roleIds = assignment.roleIds;

  if (!Array.isArray(roleIds) || !roleIds.every(isOrganizationRoleId)) {
    errors.push("Role assignment roleIds must contain supported V1 role IDs.");
  }

  if (
    !organizationRoleAssignmentStatuses.includes(
      assignment.status as OrganizationRoleAssignmentStatus,
    )
  ) {
    errors.push("Role assignment status must be supported.");
  }

  if (assignment.status === "active" && Array.isArray(roleIds) && roleIds.length === 0) {
    errors.push("Active role assignment must contain at least one supported role ID.");
  }

  if (
    !organizationRoleAssignmentSources.includes(
      assignment.source as OrganizationRoleAssignmentSource,
    )
  ) {
    errors.push("Role assignment source must be supported.");
  }

  if ("email" in assignment || "platformRole" in assignment || "permissions" in assignment) {
    errors.push("Role assignment must not contain PII, platform authority, or raw permissions.");
  }

  return {
    errors,
    valid: errors.length === 0,
  };
}

export function isOrganizationRoleId(value: unknown): value is OrganizationRoleId {
  return typeof value === "string" && organizationRoleIds.includes(value as OrganizationRoleId);
}

export function isOrganizationPermissionId(value: unknown): value is OrganizationPermissionId {
  return (
    typeof value === "string" &&
    organizationPermissionIds.includes(value as OrganizationPermissionId)
  );
}

export function legacyOrganizationRoleToV1Role(value: string): OrganizationRoleId | undefined {
  switch (value) {
    case "connected_person":
      return "connectedPerson";
    case "organization_admin":
      return "organizationAdmin";
    case "owner":
      return "organizationOwner";
    default:
      return undefined;
  }
}

function normalizeOrganizationRoleIds(
  roleIds: readonly OrganizationRoleId[],
): OrganizationRoleId[] {
  return [...new Set(roleIds)].filter(isOrganizationRoleId);
}

function isActiveGlobalPerson(person: unknown, uid: string): boolean {
  return isRecord(person) && person.uid === uid && person.status === "active";
}

function isActiveOrganization(organization: unknown): boolean {
  return isRecord(organization) && organization.status === "active";
}

function organizationIdFromRecord(organization: unknown): string {
  if (!isRecord(organization)) {
    return "";
  }

  return stringValue(organization.organizationId) || stringValue(organization.id);
}

function isSafeDocumentId(value: string): boolean {
  return value.length > 0 && value.length <= 150 && !value.includes("/");
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
